import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/icons';
import { Badge, Checkbox, ErrorState, Field, LoadingRows, PageHeader, Segmented } from '../components/ui';
import { MOCK_MODE } from '../lib/api';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { qk, useAiSettings } from '../lib/queries';
import type { AiEffort, AiSettings, AiTestResult } from '../lib/types';

const EFFORTS: { value: AiEffort; label: string }[] = [
  { value: 'off', label: 'kapalı' },
  { value: 'low', label: 'düşük' },
  { value: 'medium', label: 'orta' },
  { value: 'high', label: 'yüksek' },
  { value: 'max', label: 'maks' }
];

/** S9 — AI ayarları: model, eşik, bütçe, prompt eki ve "bu soruya ne cevap verirdi?" testi. */
export default function AiSettingsPage() {
  const settings = useAiSettings();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<AiSettings | null>(null);
  const [question, setQuestion] = useState('Kargom ne zaman gelir?');
  const [result, setResult] = useState<AiTestResult | null>(null);

  useEffect(() => {
    if (settings.data && !draft) setDraft(settings.data);
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: (value: AiSettings) => api.saveAiSettings(value),
    onSuccess: () => {
      toast.push('AI ayarları kaydedildi.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.ai });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const test = useMutation({
    mutationFn: (value: string) => api.testAi(value),
    onSuccess: (value) => setResult(value),
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  if (settings.isError) return <ErrorState message={errorMessage(settings.error)} onRetry={() => void settings.refetch()} />;
  if (settings.isLoading || !draft) return <LoadingRows rows={8} columns={2} />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings.data);

  return (
    <div>
      <PageHeader
        title="AI Ayarları"
        subtitle="Model, güven eşiği ve bütçe. Değişiklikler bir sonraki AI turunda geçerli olur."
        actions={
          <>
            <button type="button" className="btn btn-sm" onClick={() => setDraft(settings.data ?? null)} disabled={!dirty}>
              Geri al
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => save.mutate(draft)} disabled={!dirty || save.isPending}>
              <Icon name="check" size={14} />
              Kaydet
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 p-6 min-[1200px]:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Model ve davranış</h2>
              <div className="flex items-center gap-2">
                <Badge tone={draft.apiKeyConfigured ? 'ok' : 'warn'}>
                  {draft.apiKeyConfigured ? 'API anahtarı ayarlı' : 'anahtar env’den'}
                </Badge>
                <Badge tone="neutral">DeepInfra</Badge>
              </div>
            </div>
            <div className="panel-body space-y-3">
              <Field label="Model" htmlFor="ai-model" hint="F0'da model sabit; değişiklik dağıtım gerektirir.">
                <input id="ai-model" className="input mono" value={draft.model} readOnly />
              </Field>

              <div className="field">
                <span className="label">Reasoning effort</span>
                <Segmented
                  value={draft.effort}
                  options={EFFORTS}
                  onChange={(value) => setDraft({ ...draft, effort: value })}
                  label="Reasoning effort"
                />
                <span className="hint">Düşük effort daha hızlı ve ucuz; yüksek effort karmaşık sorularda daha iyi.</span>
              </div>

              <Field
                label={'Güven eşiği — ' + draft.confidenceThreshold.toFixed(2)}
                htmlFor="ai-threshold"
                hint="Bu eşiğin altında AI e-posta ister ve ticket açar."
              >
                <input
                  id="ai-threshold"
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={draft.confidenceThreshold}
                  onChange={(event) => setDraft({ ...draft, confidenceThreshold: Number(event.target.value) })}
                  className="w-full accent-[#b4551b]"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Günlük bütçe (USD)" htmlFor="ai-budget">
                  <input
                    id="ai-budget"
                    type="number"
                    min={0}
                    step={0.5}
                    className="input mono"
                    value={draft.dailyBudgetUsd}
                    onChange={(event) => setDraft({ ...draft, dailyBudgetUsd: Number(event.target.value) })}
                  />
                </Field>
                <Field label="Konuşma başına yanıt" htmlFor="ai-max">
                  <input
                    id="ai-max"
                    type="number"
                    min={1}
                    max={20}
                    className="input mono"
                    value={draft.maxRepliesPerConversation}
                    onChange={(event) => setDraft({ ...draft, maxRepliesPerConversation: Number(event.target.value) })}
                  />
                </Field>
              </div>

              <Checkbox
                id="ai-autoreply"
                checked={draft.autoReply}
                onChange={(value) => setDraft({ ...draft, autoReply: value })}
                label="Otomatik yanıt açık"
                hint="Kapalıysa AI yalnızca taslak üretir, göndermez."
              />

              <Field label="Prompt eki" htmlFor="ai-prompt" hint="Sistem promptunun sonuna eklenir (maks. 500 karakter).">
                <textarea
                  id="ai-prompt"
                  className="textarea min-h-[88px]"
                  maxLength={500}
                  value={draft.promptExtra}
                  onChange={(event) => setDraft({ ...draft, promptExtra: event.target.value })}
                />
              </Field>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">AI testi</h2>
              <span className="text-[11px] text-muted">Gerçek mesaj gönderilmez</span>
            </div>
            <div className="panel-body space-y-3">
              <p className="text-[11px] leading-4 text-muted">
                Test, gerçek bir yanıt üretir ancak hiçbir mesaj göndermez. Sonuç: cevap, karar, güven, kaynaklar ve gecikme.
              </p>
              <Field label="Müşteri sorusu" htmlFor="ai-question">
                <div className="flex gap-2">
                  <input
                    id="ai-question"
                    className="input"
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    placeholder="Ör: İade koşulları nedir?"
                  />
                  <button
                    type="button"
                    className="btn"
                    disabled={!question.trim() || test.isPending}
                    onClick={() => test.mutate(question.trim())}
                  >
                    {test.isPending ? 'Çalışıyor…' : 'Dene'}
                  </button>
                </div>
              </Field>

              {result ? (
                <div className="rounded-[4px] border border-line bg-paper p-3 text-[13px]">
                  <div className="flex items-center gap-2 text-[11px] text-muted">
                    <Badge tone={result.wouldEscalate ? 'warn' : 'ok'}>{result.wouldEscalate ? 'eskalasyon' : 'doğrudan yanıt'}</Badge>
                    {result.decision ? <Badge tone="neutral">{result.decision}</Badge> : null}
                    <span className="mono">güven {result.confidence.toFixed(2)}</span>
                    <span className="mono">{result.latencyMs} ms</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap">{result.answer}</p>
                  {result.sources.length > 0 ? (
                    <div className="mt-2 text-[11px] text-muted">Kaynaklar: {result.sources.join(' · ')}</div>
                  ) : (
                    <div className="mt-2 text-[11px] text-muted">Kaynak bulunamadı — eskalasyon beklenir.</div>
                  )}
                </div>
              ) : (
                <p className="text-[12px] text-muted">Test, seçili model ve eşikle çalışır; sonuç gerçek konuşmaya yazılmaz.</p>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Durum</h2>
            </div>
            <dl className="divide-y divide-line text-[12px]">
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Sağlayıcı</dt>
                <dd>DeepInfra</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Bugünkü harcama</dt>
                <dd className="mono">{'$0,42 / $' + draft.dailyBudgetUsd}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Ortalama gecikme</dt>
                <dd className="mono">780 ms</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Eskalasyon oranı</dt>
                <dd className="mono">%18</dd>
              </div>
            </dl>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Kurallar</h2>
            </div>
            <ul className="space-y-1.5 px-4 py-3 text-[12px] text-ink-2">
              <li className="flex gap-2">
                <Icon name="shield" size={14} className="mt-0.5 shrink-0 text-muted" />
                Fiyat/teklif sorularında AI e-posta ister ve ticket açar.
              </li>
              <li className="flex gap-2">
                <Icon name="shield" size={14} className="mt-0.5 shrink-0 text-muted" />
                Test modunda AI yalnızca izinli numaraya yanıt verir.
              </li>
              <li className="flex gap-2">
                <Icon name="shield" size={14} className="mt-0.5 shrink-0 text-muted" />
                Günlük bütçe aşılırsa AI susar, konuşma ajana düşer.
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
